import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import './EditBasicDataModal.css';

function EditBasicDataModal({ entity, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    addressNumber: '',
    addressComplement: '',
    addressCity: '',
    addressUF: '',
    addressZIP: '',
    addressNeighborhood: '',
    documentNumber: '',
    openingHours: '',
    description: '',
    lat: null,
    long: null
  });
  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [errors, setErrors] = useState({});

  // Função para formatar telefone (considerando +55)
  const formatPhone = (phone) => {
    if (!phone) return '';
    // Preserva o + se existir e remove outros caracteres não numéricos
    const hasPlus = phone.startsWith('+');
    const cleaned = phone.replace(/\D/g, '');
    
    // Remove o 55 se existir (55 no início)
    let phoneNumber = cleaned;
    if (cleaned.startsWith('55') && cleaned.length > 11) {
      phoneNumber = cleaned.substring(2);
    }
    
    if (phoneNumber.length > 10) {
      return phoneNumber.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
    } else if (phoneNumber.length > 6) {
      return phoneNumber.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
    } else if (phoneNumber.length > 2) {
      return phoneNumber.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    } else if (phoneNumber.length > 0) {
      return phoneNumber.replace(/^(\d*)/, '($1');
    }
    return phone;
  };

  // Função para formatar CEP
  const formatCEP = (cep) => {
    if (!cep) return '';
    const cleaned = cep.replace(/\D/g, '');
    if (cleaned.length > 5) {
      return cleaned.replace(/^(\d{5})(\d{0,3})/, '$1-$2');
    }
    return cleaned;
  };

  // Função para formatar documento (CPF ou CNPJ alfanumérico)
  const formatDocument = (doc) => {
    if (!doc) return '';
    // Remove apenas os caracteres de formatação, preservando alfanuméricos
    const cleaned = doc.replace(/[.\-/]/g, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // CPF: exatamente 11 dígitos numéricos
    if (/^\d{11}$/.test(cleaned)) {
      return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    
    // CNPJ alfanumérico: 14 caracteres (AA.AAA.AAA/AAAA-##)
    if (cleaned.length === 14) {
      return cleaned.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})/, '$1.$2.$3/$4-$5');
    }
    
    // Formatação parcial para CNPJ alfanumérico
    if (cleaned.length > 12) {
      return cleaned.replace(/^(.{2})(.{3})(.{3})(.{4})(.{0,2})/, '$1.$2.$3/$4-$5');
    } else if (cleaned.length > 8) {
      return cleaned.replace(/^(.{2})(.{3})(.{3})(.{0,4})/, '$1.$2.$3/$4');
    } else if (cleaned.length > 5) {
      return cleaned.replace(/^(.{2})(.{3})(.{0,3})/, '$1.$2.$3');
    } else if (cleaned.length > 2) {
      return cleaned.replace(/^(.{2})(.{0,3})/, '$1.$2');
    }
    
    return cleaned;
  };

  // Função para detectar o tipo de documento
  const getDocumentType = (doc) => {
    if (!doc) return 'Documento';
    
    // Se contém letras, provavelmente é CRMV (verificar antes de limpar)
    if (/[a-zA-Z]/.test(doc)) return 'CRMV';
    
    const cleaned = doc.replace(/\D/g, '');
    
    if (cleaned.length === 11) return 'CPF';
    if (cleaned.length === 14) return 'CNPJ';
    
    return 'Documento';
  };

  useEffect(() => {
    if (entity) {
      setFormData({
        name: entity.name || '',
        phone: formatPhone(entity.phone || ''),
        address: entity.address || '',
        addressNumber: entity.addressNumber || '',
        addressComplement: entity.addressComplement || '',
        addressCity: entity.addressCity || '',
        addressUF: entity.addressUF || '',
        addressZIP: formatCEP(entity.addressZIP || ''),
        addressNeighborhood: entity.addressNeighborhood || '',
        documentNumber: entity.documentNumber || '',
        openingHours: entity.openingHours || '',
        description: entity.description || '',
        lat: entity.lat || null,
        long: entity.long || null
      });
    }
  }, [entity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo ao digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Nome do negócio é obrigatório';
    }

    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!formData.address || formData.address.trim() === '') {
      newErrors.address = 'Endereço é obrigatório';
    }

    if (!formData.addressNumber || formData.addressNumber.trim() === '') {
      newErrors.addressNumber = 'Número é obrigatório';
    }

    if (!formData.addressCity || formData.addressCity.trim() === '') {
      newErrors.addressCity = 'Cidade é obrigatória';
    }

    if (!formData.addressUF || formData.addressUF.trim() === '') {
      newErrors.addressUF = 'UF é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCoordinatesFromAddress = async (address, addressNumber, addressNeighborhood, city, state) => {
    try {
      const fullAddress = `${address}, ${addressNumber}, ${addressNeighborhood}, ${city}, ${state}, Brasil`;
      const apiKey = 'AIzaSyA3eQOASrGy5ysiPCB8rP3HZhQbRGv9Omw';
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`;
      
      console.log('Buscando coordenadas para:', fullAddress);
      console.log('URL da API:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('Resposta da API do Google:', data);
      
      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        console.log('Localização encontrada:', location);
        return {
          lat: location.lat,
          long: location.lng
        };
      } else {
        console.log('Status da API:', data.status);
        console.log('Mensagem de erro:', data.error_message);
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Adicionar +55 no telefone mantendo formatação
      const cleanedPhone = formData.phone.replace(/\D/g, '');
      
      // Buscar coordenadas do endereço
      toast.loading('Buscando coordenadas do endereço...');
      const coordinates = await getCoordinatesFromAddress(
        formData.address,
        formData.addressNumber,
        formData.addressNeighborhood,
        formData.addressCity,
        formData.addressUF
      );
      toast.dismiss();
      
      if (!coordinates) {
        toast.error('Não foi possível obter as coordenadas. Salvando sem atualizar localização.');
      }
      
      const dataToSave = {
        ...formData,
        phone: `+55${cleanedPhone}`,
        addressZIP: formData.addressZIP,
        lat: coordinates?.lat ? Number(coordinates.lat).toFixed(5) : formData.lat || null,
        long: coordinates?.long ? Number(coordinates.long).toFixed(5) : formData.long || null
      };
      
      console.log('Dados a serem salvos:', dataToSave);
      console.log('Coordenadas obtidas:', coordinates);
      
      await onSave(dataToSave);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    
    // Formatar: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (value.length > 10) {
      value = value.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
    } else if (value.length > 6) {
      value = value.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
    } else if (value.length > 2) {
      value = value.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    } else if (value.length > 0) {
      value = value.replace(/^(\d*)/, '($1');
    }
    
    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleZIPChange = async (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    
    // Formatar: XXXXX-XXX
    if (value.length > 5) {
      value = value.replace(/^(\d{5})(\d{0,3})/, '$1-$2');
    }
    
    setFormData(prev => ({ ...prev, addressZIP: value }));

    // Buscar CEP quando tiver 8 dígitos
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          toast.error('CEP não encontrado');
        } else {
          setFormData(prev => ({
            ...prev,
            address: data.logradouro || prev.address,
            addressCity: data.localidade || prev.addressCity,
            addressUF: data.uf || prev.addressUF,
            addressNeighborhood: data.bairro || prev.addressNeighborhood,
            addressComplement: data.complemento || prev.addressComplement
          }));
          
          // Limpar erros dos campos preenchidos
          setErrors(prev => ({
            ...prev,
            address: '',
            addressCity: '',
            addressUF: ''
          }));
          
          toast.success('Endereço encontrado!');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        toast.error('Erro ao buscar CEP. Tente novamente.');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const handleCNPJChange = (e) => {
    // Remove formatação preservando alfanuméricos (formato: AA.AAA.AAA/AAAA-##)
    let value = e.target.value.replace(/[.\-/]/g, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (value.length > 14) value = value.slice(0, 14);
    
    // Formatar: AA.AAA.AAA/AAAA-##
    if (value.length > 12) {
      value = value.replace(/^(.{2})(.{3})(.{3})(.{4})(.{0,2})/, '$1.$2.$3/$4-$5');
    } else if (value.length > 8) {
      value = value.replace(/^(.{2})(.{3})(.{3})(.{0,4})/, '$1.$2.$3/$4');
    } else if (value.length > 5) {
      value = value.replace(/^(.{2})(.{3})(.{0,3})/, '$1.$2.$3');
    } else if (value.length > 2) {
      value = value.replace(/^(.{2})(.{0,3})/, '$1.$2');
    }
    
    setFormData(prev => ({ ...prev, documentNumber: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-edit" onClick={onClose}>
      <div className="modal-content-edit" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-edit">
          <div>
            <h2 className="modal-title-edit">Editar Dados Básicos</h2>
            <p className="modal-subtitle-edit">Atualize as informações do seu negócio</p>
          </div>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form-edit">
          <div className="form-grid-edit">
            {/* Nome do Negócio */}
            <div className="form-group-edit full-width">
              <label className="form-label-edit">
                Nome do Negócio <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`form-input-edit ${errors.name ? 'input-error' : ''}`}
                placeholder="Nome do seu negócio"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            {/* Telefone */}
            <div className="form-group-edit">
              <label className="form-label-edit">
                Telefone <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={`form-input-edit ${errors.phone ? 'input-error' : ''}`}
                placeholder="(00) 00000-0000"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            {/* Endereço */}
            <div className="form-group-edit full-width">
              <label className="form-label-edit">
                Endereço <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={`form-input-edit ${errors.address ? 'input-error' : ''}`}
                placeholder="Rua, Avenida, etc."
              />
              {errors.address && <span className="error-message">{errors.address}</span>}
            </div>

            {/* Número */}
            <div className="form-group-edit">
              <label className="form-label-edit">
                Número <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="addressNumber"
                value={formData.addressNumber}
                onChange={handleChange}
                className={`form-input-edit ${errors.addressNumber ? 'input-error' : ''}`}
                placeholder="123"
              />
              {errors.addressNumber && <span className="error-message">{errors.addressNumber}</span>}
            </div>

            {/* Complemento */}
            <div className="form-group-edit">
              <label className="form-label-edit">Complemento</label>
              <input
                type="text"
                name="addressComplement"
                value={formData.addressComplement}
                onChange={handleChange}
                className="form-input-edit"
                placeholder="Apto, Sala, etc."
              />
            </div>

            {/* Cidade */}
            <div className="form-group-edit">
              <label className="form-label-edit">
                Cidade <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="addressCity"
                value={formData.addressCity}
                onChange={handleChange}
                className={`form-input-edit ${errors.addressCity ? 'input-error' : ''}`}
                placeholder="São Paulo"
              />
              {errors.addressCity && <span className="error-message">{errors.addressCity}</span>}
            </div>

            {/* UF */}
            <div className="form-group-edit">
              <label className="form-label-edit">
                UF <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="addressUF"
                value={formData.addressUF}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 2);
                  setFormData(prev => ({ ...prev, addressUF: value }));
                  if (errors.addressUF) {
                    setErrors(prev => ({ ...prev, addressUF: '' }));
                  }
                }}
                className={`form-input-edit ${errors.addressUF ? 'input-error' : ''}`}
                placeholder="SP"
                maxLength="2"
              />
              {errors.addressUF && <span className="error-message">{errors.addressUF}</span>}
            </div>

            {/* CEP */}
            <div className="form-group-edit">
              <label className="form-label-edit">CEP</label>
              <div className="input-with-icon">
                <input
                  type="text"
                  name="addressZIP"
                  value={formData.addressZIP}
                  onChange={handleZIPChange}
                  className="form-input-edit"
                  placeholder="00000-000"
                  disabled={loadingCep}
                />
                {loadingCep && (
                  <div className="input-icon">
                    <svg className="spinner-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" opacity="0.25"/>
                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* CNPJ/CPF/CRMV */}
            <div className="form-group-edit">
              <label className="form-label-edit">{entity?.documentType || 'Documento'}</label>
              <input
                type="text"
                name="documentNumber"
                value={formData.documentNumber}
                className="form-input-edit"
                placeholder="00.000.000/0000-00"
                disabled
                title="Este campo não pode ser alterado"
              />
            </div>

            {/* Horário de Funcionamento */}
            <div className="form-group-edit full-width">
              <label className="form-label-edit">Horário de Funcionamento</label>
              <input
                type="text"
                name="openingHours"
                value={formData.openingHours}
                onChange={handleChange}
                className="form-input-edit"
                placeholder="Ex: Segunda a Sexta: 8h às 18h"
              />
            </div>

            {/* Descrição */}
            <div className="form-group-edit full-width">
              <label className="form-label-edit">Descrição</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-textarea-edit"
                placeholder="Descreva seu negócio..."
                rows="4"
              />
            </div>
          </div>

          <div className="modal-actions-edit">
            <button 
              type="button" 
              className="btn-cancel-edit" 
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn-save-edit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="spinner-edit" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                  </svg>
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBasicDataModal;
